import { Quaternion, Sprite, SpriteMaterial, Vector3 } from 'three';
import type { Scene } from 'three';
import { FLAG, MODE, RIDER_COLORS } from '@thrasher/shared';
import type { RiderSnapshot, RosterEntry } from '@thrasher/shared';
import { RiderAvatar } from './RiderAvatar.js';
import type { PoseInput } from './RiderAvatar.js';
import { nameTagTexture } from './textures.js';

interface Sample {
  t: number;
  p: Vector3;
  q: Quaternion;
  v: Vector3;
  m: number;
  f: number;
  k: number;
  a: number;
  hp: number;
  prot: boolean;
}

export function hashSeed(id: string): number {
  let hash = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** A networked rider: snapshot buffer, interpolated transform, avatar and name tag. */
export class RemoteRider {
  public avatar: RiderAvatar;
  public readonly position = new Vector3();
  public readonly quaternion = new Quaternion();
  public readonly velocity = new Vector3();
  public mode: number = MODE.ground;
  public flags = 0;
  public trick = 0;
  public phase = 0;
  public hp = 100;
  public protected = false;
  public hitFlash = 0;
  private readonly samples: Sample[] = [];
  private readonly tag: Sprite;
  private previousFlags = 0;
  private entry: RosterEntry;

  public constructor(
    private readonly scene: Scene,
    entry: RosterEntry,
  ) {
    this.entry = entry;
    this.avatar = this.makeAvatar(entry);
    const material = new SpriteMaterial({
      map: nameTagTexture(entry.name, RIDER_COLORS[entry.color % RIDER_COLORS.length]!),
      depthTest: true,
      transparent: true,
    });
    this.tag = new Sprite(material);
    this.tag.scale.set(2.2, 0.55, 1);
    this.tag.renderOrder = 5;
    scene.add(this.tag);
  }

  private makeAvatar(entry: RosterEntry): RiderAvatar {
    const avatar = new RiderAvatar({
      ride: entry.ride,
      color: RIDER_COLORS[entry.color % RIDER_COLORS.length]!,
      seed: hashSeed(entry.id),
    });
    this.scene.add(avatar.root);
    return avatar;
  }

  public updateEntry(entry: RosterEntry): void {
    const rideChanged = entry.ride !== this.entry.ride;
    const nameChanged = entry.name !== this.entry.name || entry.color !== this.entry.color;
    this.entry = entry;
    if (rideChanged) {
      this.avatar.dispose();
      this.avatar = this.makeAvatar(entry);
    }
    if (nameChanged) {
      const material = this.tag.material;
      material.map?.dispose();
      material.map = nameTagTexture(entry.name, RIDER_COLORS[entry.color % RIDER_COLORS.length]!);
      material.needsUpdate = true;
    }
  }

  public get name(): string {
    return this.entry.name;
  }

  public get color(): number {
    return this.entry.color;
  }

  public push(snapshot: RiderSnapshot, t: number): void {
    const last = this.samples[this.samples.length - 1];
    if (last && t <= last.t) return;
    this.samples.push({
      t,
      p: new Vector3(...snapshot.p),
      q: new Quaternion(...snapshot.q),
      v: new Vector3(...snapshot.v),
      m: snapshot.m,
      f: snapshot.f,
      k: snapshot.k,
      a: snapshot.a,
      hp: snapshot.hp,
      prot: snapshot.prot,
    });
    while (this.samples.length > 40) this.samples.shift();
  }

  /** Snaps the buffer after a teleport so the rider does not glide across the park. */
  public teleport(): void {
    this.samples.splice(0, Math.max(0, this.samples.length - 1));
  }

  public update(dt: number, renderTime: number, groundAt: PoseInput['groundAt']): void {
    const samples = this.samples;
    if (samples.length === 0) return;
    let a = samples[0]!;
    let b = a;
    for (let i = samples.length - 1; i >= 0; i -= 1) {
      if (samples[i]!.t <= renderTime) {
        a = samples[i]!;
        b = samples[i + 1] ?? a;
        break;
      }
    }
    let blend = 0;
    if (a === b) {
      // Extrapolate briefly past the newest sample.
      const ahead = Math.min(0.25, Math.max(0, (renderTime - a.t) / 1000));
      this.position
        .copy(a.p)
        .addScaledVector(
          a.v,
          a.m === MODE.grind || a.m === MODE.ground || a.m === MODE.air ? ahead : 0,
        );
      this.quaternion.copy(a.q);
    } else {
      const span = b.t - a.t;
      const alpha = span > 0 ? Math.min(1, Math.max(0, (renderTime - a.t) / span)) : 1;
      blend = alpha;
      // Treat a large jump as a teleport rather than sliding through the world.
      if (a.p.distanceToSquared(b.p) > 100) this.position.copy(alpha < 0.5 ? a.p : b.p);
      else this.position.lerpVectors(a.p, b.p, alpha);
      this.quaternion.slerpQuaternions(a.q, b.q, alpha);
    }
    const current = blend < 0.5 ? a : b;
    this.velocity.copy(current.v);
    this.mode = current.m;
    this.flags = current.f;
    this.trick = current.k;
    // Trick phases restart between tricks, so only blend while they move forward.
    this.phase = b.a >= a.a && b.k === a.k ? a.a + (b.a - a.a) * blend : current.a;
    this.hp = current.hp;
    this.protected = current.prot;

    const rising = this.flags & ~this.previousFlags;
    if (rising & FLAG.punch) this.avatar.attack('punch');
    if (rising & FLAG.kick) this.avatar.attack('kick');
    this.previousFlags = this.flags;
    this.hitFlash = Math.max(0, this.hitFlash - dt * 4);

    this.avatar.root.position.copy(this.position);
    this.avatar.root.quaternion.copy(this.quaternion);
    this.avatar.update(dt, {
      mode: this.mode,
      flags: this.flags,
      trick: this.trick,
      phase: this.phase,
      speed: this.velocity.length(),
      steer: 0,
      attack: null,
      hit: this.hitFlash,
      protected: this.protected,
      groundAt,
    });
    this.tag.position.copy(this.position).add(new Vector3(0, 2.35, 0));
  }

  public dispose(): void {
    this.avatar.dispose();
    this.tag.material.map?.dispose();
    this.tag.material.dispose();
    this.scene.remove(this.tag);
  }
}
